(() => {
  const doc = document;
  const body = doc.body;
  const drawerOverlay = doc.querySelector('.drawer-overlay');
  let revealObserver = null;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (selector, root = doc) => root.querySelector(selector);
  const qsa = (selector, root = doc) => Array.from(root.querySelectorAll(selector));
  const header = qs('[data-site-header]');

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    })[character]);

  const routeWithJson = (route, fallback) => {
    const base = route || fallback;
    return base.endsWith('.js') || base.endsWith('.json') ? base : `${base}.js`;
  };

  const setThemeSegment = (segment) => {
    try {
      window.localStorage.setItem('recollectSegment', segment);
    } catch (error) {
      return;
    }

    const isPreCollect = segment === 'pre-collect';
    doc.documentElement.classList.toggle('pre-collect-theme', isPreCollect);
    body.classList.toggle('pre-collect-theme', isPreCollect);
    qsa('[data-mega]').forEach((menu) => {
      menu.setAttribute('data-line-scope', isPreCollect ? 'pre-collect' : 're-collect');
    });
  };

  if (doc.documentElement.classList.contains('pre-collect-theme')) {
    body.classList.add('pre-collect-theme');
  }

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency
    }).format(cents / 100);
  };

  const hidePreloader = () => {
    const preloader = qs('[data-preloader]');
    if (!preloader || preloader.dataset.hidden === 'true') return;
    preloader.dataset.hidden = 'true';

    window.setTimeout(() => {
      preloader.classList.add('is-hidden');
    }, 450);

    window.setTimeout(() => {
      preloader.remove();
    }, 1400);
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', hidePreloader, { once: true });
  } else {
    hidePreloader();
  }
  window.setTimeout(hidePreloader, 1800);

  const getDrawer = (name) => qs(`[data-drawer="${name}"]`);

  const closeDrawers = () => {
    qsa('[data-drawer].is-open').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });

    if (header) header.classList.remove('is-menu-open');

    if (drawerOverlay) {
      drawerOverlay.classList.remove('is-visible');
      drawerOverlay.hidden = true;
    }

    body.classList.remove('drawer-open', 'menu-drawer-open', 'search-drawer-open', 'cart-drawer-open');
    delete body.dataset.activeDrawer;
  };

  let activeSizeChartTrigger = null;

  const updateSizeChart = (modal) => {
    const category = modal.dataset.activeCategory || 'tops';
    const unit = modal.dataset.activeUnit || 'us';
    const activeTable = `${category}-${unit}`;

    qsa('[data-size-chart-category]', modal).forEach((button) => {
      const isActive = button.dataset.sizeChartCategory === category;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });

    qsa('[data-size-chart-unit]', modal).forEach((button) => {
      const isActive = button.dataset.sizeChartUnit === unit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    });

    qsa('[data-size-chart-table]', modal).forEach((table) => {
      table.classList.toggle('is-active', table.dataset.sizeChartTable === activeTable);
    });
  };

  const openSizeChart = (modal, trigger) => {
    if (!modal) return;
    activeSizeChartTrigger = trigger || null;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    body.classList.add('size-chart-open');
    updateSizeChart(modal);

    const dialog = qs('.size-chart-modal__dialog', modal);
    if (dialog) dialog.focus({ preventScroll: true });
  };

  const closeSizeChart = () => {
    const modal = qs('[data-size-chart-modal].is-open');
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    body.classList.remove('size-chart-open');

    if (activeSizeChartTrigger) {
      activeSizeChartTrigger.focus({ preventScroll: true });
    }

    activeSizeChartTrigger = null;
  };

  const openDrawer = (name) => {
    const drawer = getDrawer(name);
    if (!drawer) return;

    closeDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('drawer-open');
    body.classList.add(`${name}-drawer-open`);
    body.dataset.activeDrawer = name;
    if (name === 'menu' && header) {
      header.classList.add('is-menu-open');
      header.classList.remove('is-hidden');
    }

    if (drawerOverlay) {
      drawerOverlay.hidden = false;
      drawerOverlay.classList.add('is-visible');
    }

    const focusable = drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) {
      window.setTimeout(() => {
        if (drawer.classList.contains('is-open')) focusable.focus({ preventScroll: true });
      }, 40);
    }
  };

  doc.addEventListener('click', (event) => {
    const segmentLink = event.target.closest('[data-segment-link]');
    if (segmentLink) {
      setThemeSegment(segmentLink.dataset.segmentLink);
    }

    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      closeDrawers();
    }

    const sizeChartOpener = event.target.closest('[data-size-chart-open]');
    if (sizeChartOpener) {
      event.preventDefault();
      openSizeChart(doc.getElementById(sizeChartOpener.dataset.sizeChartOpen), sizeChartOpener);
      return;
    }

    if (event.target.closest('[data-size-chart-close]')) {
      event.preventDefault();
      closeSizeChart();
      return;
    }

    const categoryButton = event.target.closest('[data-size-chart-category]');
    if (categoryButton) {
      const modal = categoryButton.closest('[data-size-chart-modal]');
      modal.dataset.activeCategory = categoryButton.dataset.sizeChartCategory;
      updateSizeChart(modal);
      return;
    }

    const unitButton = event.target.closest('[data-size-chart-unit]');
    if (unitButton) {
      const modal = unitButton.closest('[data-size-chart-modal]');
      modal.dataset.activeUnit = unitButton.dataset.sizeChartUnit;
      updateSizeChart(modal);
    }
  });

  doc.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeDrawers();
      closeSizeChart();
    }
  });

  let lastHeaderY = window.scrollY;
  const updateHeader = () => {
    if (!header) return;
    const currentY = window.scrollY;
    const isScrolled = currentY > 8;
    const isScrollingDown = currentY > lastHeaderY && currentY > 16;

    header.classList.toggle('is-scrolled', isScrolled);
    header.classList.remove('is-hidden');
    body.classList.toggle('is-at-top', !isScrolled);
    body.classList.toggle('has-scrolled', isScrolled);
    body.classList.toggle('is-scrolling-down', isScrollingDown && isScrolled);
    lastHeaderY = Math.max(0, currentY);
  };
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });

  const observeReveal = (root = doc) => {
    const elements = qsa('[data-reveal-section]:not(.is-visible)', root);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px'
      });
    }

    elements.forEach((element) => revealObserver.observe(element));
  };

  observeReveal();

  const initEditorialDeck = () => {
    const deck = qs('[data-editorial-deck]');
    if (!deck || prefersReducedMotion) return;

    const cards = qsa('[data-deck-card]', deck);
    if (cards.length < 2) return;

    const dots = qsa('[data-deck-go]', deck);
    const current = qs('[data-deck-current]', deck);
    let activeIndex = 0;
    let isAnimating = false;
    let touchStartY = 0;
    let touchHandled = false;

    const setDeckTheme = () => {
      const activeCard = cards[activeIndex];
      const lightCard = activeCard && !activeCard.classList.contains('editorial-panel');
      body.classList.toggle('deck-light', Boolean(lightCard));
    };

    const setActiveCard = (nextIndex) => {
      const clampedIndex = Math.max(0, Math.min(cards.length - 1, nextIndex));
      activeIndex = clampedIndex;

      cards.forEach((card, index) => {
        card.classList.toggle('is-active', index === activeIndex);
        card.classList.toggle('is-before', index < activeIndex);
        card.classList.toggle('is-after', index > activeIndex);
      });

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeIndex);
      });

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      deck.dataset.activeIndex = String(activeIndex);
      setDeckTheme();
    };

    const stepDeck = (direction) => {
      if (isAnimating) return false;
      const nextIndex = activeIndex + direction;
      if (nextIndex < 0 || nextIndex >= cards.length) return false;

      isAnimating = true;
      setActiveCard(nextIndex);
      window.setTimeout(() => {
        isAnimating = false;
      }, 880);
      return true;
    };

    const deckIsPrimaryViewport = () => {
      const rect = deck.getBoundingClientRect();
      // Engage slightly before the deck sits exactly at the top. Anything
      // occupying a few pixels above it used to stop the deck taking over the
      // wheel at all, which sent the page straight past it to the footer.
      return rect.top <= 12 && rect.top > -rect.height * 0.5 && rect.bottom >= window.innerHeight * 0.72;
    };

    // A single trackpad flick emits dozens of wheel events. Accumulating them
    // means one gesture moves exactly one card, rather than none (swallowed by
    // the animation lock) or several at once.
    let wheelDelta = 0;
    let wheelResetTimer = null;
    const WHEEL_STEP = 34;

    window.addEventListener('wheel', (event) => {
      if (!deckIsPrimaryViewport()) return;
      if (body.classList.contains('drawer-open') || body.classList.contains('size-chart-open') || body.classList.contains('product-lightbox-open')) return;
      if (event.target.closest('input, textarea, select, [data-drawer]')) return;

      const direction = event.deltaY > 0 ? 1 : -1;
      const canMoveInsideDeck =
        (direction > 0 && activeIndex < cards.length - 1) ||
        (direction < 0 && activeIndex > 0);

      if (!canMoveInsideDeck) {
        wheelDelta = 0;
        return;
      }

      // Hold the page still for the whole gesture, including while a card is
      // mid transition, so it cannot slip past the deck between steps.
      event.preventDefault();
      if (isAnimating) return;

      wheelDelta += event.deltaY;
      window.clearTimeout(wheelResetTimer);
      wheelResetTimer = window.setTimeout(() => {
        wheelDelta = 0;
      }, 180);

      if (Math.abs(wheelDelta) < WHEEL_STEP) return;

      wheelDelta = 0;
      stepDeck(direction);
    }, { passive: false });

    deck.addEventListener('touchstart', (event) => {
      touchStartY = event.touches[0]?.clientY || 0;
      touchHandled = false;
    }, { passive: true });

    deck.addEventListener('touchmove', (event) => {
      if (!deckIsPrimaryViewport()) return;
      if (body.classList.contains('drawer-open') || body.classList.contains('size-chart-open') || body.classList.contains('product-lightbox-open')) return;
      if (event.target.closest('input, textarea, select, [data-drawer]')) return;

      const touchY = event.touches[0]?.clientY || touchStartY;
      const delta = touchStartY - touchY;
      if (Math.abs(delta) < 8) return;

      const direction = delta > 0 ? 1 : -1;
      const canMoveInsideDeck =
        (direction > 0 && activeIndex < cards.length - 1) ||
        (direction < 0 && activeIndex > 0);

      if (!canMoveInsideDeck) return;

      event.preventDefault();
      if (touchHandled || isAnimating || Math.abs(delta) < 42) return;

      touchHandled = stepDeck(direction);
    }, { passive: false });

    deck.addEventListener('touchend', () => {
      touchHandled = false;
    });

    doc.addEventListener('keydown', (event) => {
      if (!deckIsPrimaryViewport()) return;
      if (body.classList.contains('drawer-open') || body.classList.contains('size-chart-open') || body.classList.contains('product-lightbox-open')) return;
      if (['ArrowDown', 'PageDown', ' '].includes(event.key)) {
        if (activeIndex < cards.length - 1) {
          event.preventDefault();
          stepDeck(1);
        }
      }

      if (['ArrowUp', 'PageUp'].includes(event.key)) {
        if (activeIndex > 0) {
          event.preventDefault();
          stepDeck(-1);
        }
      }
    });

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const nextIndex = Number(dot.dataset.deckGo);
        if (Number.isNaN(nextIndex)) return;
        deck.scrollIntoView({ block: 'start' });
        setActiveCard(nextIndex);
      });
    });

    setActiveCard(0);
  };

  initEditorialDeck();

  const updateCartBadges = (cart) => {
    qsa('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.setAttribute('data-count', cart.item_count);
      element.classList.remove('is-bumped');
      void element.offsetWidth;
      element.classList.add('is-bumped');
      window.setTimeout(() => element.classList.remove('is-bumped'), 420);
    });

    const cartTotal = qs('[data-cart-total]');
    if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price);

    const cartUpsell = qs('[data-cart-upsell]');
    if (cartUpsell) cartUpsell.hidden = cart.item_count === 0;
  };

  const renderCartItems = (cart) => {
    const container = qs('[data-cart-items]');
    if (!container) return;

    if (!cart.items.length) {
      container.innerHTML = `
        <div class="cart-empty">
          <p>${escapeHtml(window.themeStrings?.cartEmpty || 'Your shopping bag is empty.')}</p>
          <a class="button button--full" href="/collections/all">${escapeHtml(window.themeStrings?.continueShopping || 'Continue shopping')}</a>
        </div>
      `;
      return;
    }

    container.innerHTML = cart.items.map((item, index) => {
      const image = item.image
        ? `<img class="cart-item__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
        : '';
      const quantity = item.quantity;

      return `
        <article class="cart-item" data-cart-item>
          <a class="cart-item__media" href="${escapeHtml(item.url)}">${image}</a>
          <div class="cart-item__content">
            <a class="cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
            ${variant}
            <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
            <div class="quantity-control">
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${quantity - 1}" aria-label="Decrease quantity">−</button>
              <span>${quantity}</span>
              <button type="button" data-cart-change data-line="${index + 1}" data-quantity="${quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button type="button" class="text-button" data-cart-change data-line="${index + 1}" data-quantity="0">Remove</button>
          </div>
        </article>
      `;
    }).join('');
  };

  const getCart = async () => {
    const response = await fetch(routeWithJson(window.themeRoutes?.cart, '/cart'), {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    updateCartBadges(cart);
    renderCartItems(cart);
    const checkoutButton = qs('.cart-drawer__footer button[name="checkout"]');
    if (checkoutButton) checkoutButton.disabled = cart.item_count === 0;
    return cart;
  };

  const setFormState = (form, loading, message = '') => {
    const button = qs('[data-add-to-cart]', form);
    const messageElement = qs('[data-form-message]', form);

    if (button) {
      button.disabled = loading;
      button.textContent = loading
        ? (window.themeStrings?.adding || 'Adding')
        : (button.dataset.defaultText || window.themeStrings?.addToCart || 'Add to bag');
    }

    if (messageElement) messageElement.textContent = message;
  };

  qsa('[data-add-to-cart]').forEach((button) => {
    button.dataset.defaultText = button.textContent.trim();
  });

  doc.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    event.preventDefault();
    setFormState(form, true);

    try {
      const response = await fetch(routeWithJson(window.themeRoutes?.cartAdd, '/cart/add'), {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Unable to add item');
      }

      await refreshCart();
      setFormState(form, false);

      if (body.dataset.cartDrawerEnabled === 'true') {
        openDrawer('cart');
      }
    } catch (error) {
      setFormState(form, false, error.message);
    }
  });

  doc.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-change]');
    if (!control) return;

    event.preventDefault();
    control.disabled = true;

    try {
      const response = await fetch(routeWithJson(window.themeRoutes?.cartChange, '/cart/change'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line: Number(control.dataset.line),
          quantity: Number(control.dataset.quantity)
        })
      });

      if (!response.ok) throw new Error('Unable to update cart');
      const cart = await response.json();
      updateCartBadges(cart);
      renderCartItems(cart);
    } catch (error) {
      control.disabled = false;
    }
  });

  qsa('[data-product-form]').forEach((form) => {
    const picker = qs('[data-variant-picker]', form);
    const variantScript = qs('[data-variant-json]', form);
    if (!picker || !variantScript) return;

    const variants = JSON.parse(variantScript.textContent);
    const hiddenInput = qs('[data-variant-id]', form);
    const button = qs('[data-add-to-cart]', form);

    const updateVariant = () => {
      const selectedOptions = qsa('fieldset', picker).map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });

      const selectedVariant = variants.find((variant) =>
        variant.options.every((option, index) => option === selectedOptions[index])
      );

      if (hiddenInput && selectedVariant) hiddenInput.value = selectedVariant.id;

      if (button) {
        const available = Boolean(selectedVariant && selectedVariant.available);
        button.disabled = !available;
        button.dataset.defaultText = available
          ? (window.themeStrings?.addToCart || 'Add to bag')
          : (selectedVariant ? window.themeStrings?.soldOut : window.themeStrings?.unavailable);
        button.textContent = button.dataset.defaultText;
      }
    };

    picker.addEventListener('change', updateVariant);
    updateVariant();
  });

  const debounce = (callback, delay = 180) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), delay);
    };
  };

  qsa('[data-predictive-search-input]').forEach((input) => {
    const drawer = input.closest('.search-drawer');
    const results = qs('[data-predictive-search-results]', drawer);
    if (!results) return;

    const renderPredictiveProducts = (products) => {
      if (!products.length) {
        results.innerHTML = `<p>${escapeHtml(window.themeStrings?.noResults || 'No results found')}</p>`;
        return;
      }

      results.innerHTML = products.map((product) => {
        let image = product.image || '';
        if (image && typeof image === 'object') image = image.url || image.src || '';
        if (!image && product.featured_image) {
          image = typeof product.featured_image === 'string'
            ? product.featured_image
            : product.featured_image.url || product.featured_image.src || '';
        }
        const price = typeof product.price === 'number'
          ? formatMoney(product.price)
          : product.price || (typeof product.price_min === 'number' ? formatMoney(product.price_min) : product.price_min || '');

        return `
          <a class="predictive-search__item" href="${escapeHtml(product.url)}">
            ${image ? `<img class="predictive-search__image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
            <span>
              <strong>${escapeHtml(product.title)}</strong>
              ${price ? `<small>${escapeHtml(price)}</small>` : ''}
            </span>
          </a>
        `;
      }).join('');
    };

    const performSearch = async (query) => {
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }

      results.setAttribute('aria-busy', 'true');
      const endpoint = `${window.themeRoutes?.predictiveSearch || '/search/suggest'}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`;

      try {
        const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        renderPredictiveProducts(data.resources?.results?.products || []);
      } catch (error) {
        results.innerHTML = `<p>${escapeHtml(window.themeStrings?.noResults || 'No results found')}</p>`;
      } finally {
        results.removeAttribute('aria-busy');
      }
    };

    const search = debounce(() => performSearch(input.value.trim()));

    input.addEventListener('input', search);

    qsa('[data-search-suggestion]', drawer).forEach((button) => {
      button.addEventListener('click', () => {
        const query = button.dataset.searchSuggestion || button.textContent.trim();
        input.value = query;
        input.focus({ preventScroll: true });
        performSearch(query);
      });
    });
  });

  const footerAccordionQuery = window.matchMedia('(max-width: 989px)');
  const syncFooterAccordions = () => {
    qsa('[data-footer-accordion]').forEach((accordion) => {
      if (footerAccordionQuery.matches) {
        accordion.removeAttribute('open');
      } else {
        accordion.setAttribute('open', '');
      }
    });
  };

  if (qsa('[data-footer-accordion]').length) {
    syncFooterAccordions();
    if (typeof footerAccordionQuery.addEventListener === 'function') {
      footerAccordionQuery.addEventListener('change', syncFooterAccordions);
    } else if (typeof footerAccordionQuery.addListener === 'function') {
      footerAccordionQuery.addListener(syncFooterAccordions);
    }
  }

  const filterToggle = qs('[data-filter-toggle]');
  const filterForm = qs('[data-filter-form]');
  if (filterToggle && filterForm) {
    filterToggle.addEventListener('click', () => {
      filterForm.classList.toggle('is-open');
      body.classList.toggle('filters-open', filterForm.classList.contains('is-open'));
    });
  }

  if (filterForm) {
    qsa('select, input[type="checkbox"]', filterForm).forEach((field) => {
      field.addEventListener('change', () => filterForm.submit());
    });
  }

  doc.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-load-more]');
    if (!link) return;

    event.preventDefault();
    link.classList.add('is-loading');
    link.textContent = window.themeStrings?.adding || 'Loading';

    try {
      const response = await fetch(link.href);
      if (!response.ok) throw new Error('Unable to load more products');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextItems = qsa('[data-product-grid] > *', parsed);
      const currentGrid = qs('[data-product-grid]');

      nextItems.forEach((item) => currentGrid.appendChild(item));

      const nextPagination = qs('[data-pagination]', parsed);
      const currentPagination = qs('[data-pagination]');
      if (currentPagination) {
        currentPagination.innerHTML = nextPagination ? nextPagination.innerHTML : '';
      }

      observeReveal(currentGrid);
    } catch (error) {
      link.textContent = 'Try again';
      link.classList.remove('is-loading');
    }
  });
})();

/* ==========================================================================
   Product gallery and lightbox
   ========================================================================== */

(() => {
  const doc = document;
  const body = doc.body;
  const mobile = window.matchMedia('(max-width: 989px)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (sel, root = doc) => root.querySelector(sel);
  const qsa = (sel, root = doc) => Array.from(root.querySelectorAll(sel));
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const onMediaChange = (query, callback) => {
    if (!query) return;
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', callback);
    } else if (typeof query.addListener === 'function') {
      query.addListener(callback);
    }
  };

  const initProductGallery = (gallery) => {
    if (gallery.dataset.productGalleryBound === 'true') return;
    gallery.dataset.productGalleryBound = 'true';

    const shell = gallery.closest('[data-product-gallery-shell]') || gallery.parentElement;
    const slides = qsa('[data-product-slide]', gallery);
    const dots = qsa('[data-product-gallery-go]', shell);
    const previous = qs('[data-product-gallery-prev]', shell);
    const next = qs('[data-product-gallery-next]', shell);
    if (!slides.length) return;

    let activeIndex = 0;
    let scrollFrame = null;

    const updateControls = () => {
      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });

      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === slides.length - 1;
    };

    const setActive = (index, shouldScroll = false) => {
      activeIndex = clamp(index, 0, slides.length - 1);
      updateControls();

      if (!shouldScroll || !mobile.matches) return;
      gallery.scrollTo({
        left: slides[activeIndex].offsetLeft,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    };

    const readActiveSlide = () => {
      scrollFrame = null;
      const galleryRect = gallery.getBoundingClientRect();
      const galleryCenter = galleryRect.left + galleryRect.width / 2;
      let closestIndex = activeIndex;
      let closestDistance = Infinity;

      slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(slideCenter - galleryCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex) setActive(closestIndex);
    };

    gallery.addEventListener('scroll', () => {
      if (!mobile.matches || scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(readActiveSlide);
    }, { passive: true });

    if (previous) previous.addEventListener('click', () => setActive(activeIndex - 1, true));
    if (next) next.addEventListener('click', () => setActive(activeIndex + 1, true));

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const nextIndex = Number(dot.dataset.productGalleryGo);
        if (!Number.isNaN(nextIndex)) setActive(nextIndex, true);
      });
    });

    window.addEventListener('resize', () => {
      if (mobile.matches) setActive(activeIndex, true);
      else updateControls();
    }, { passive: true });

    setActive(0);
  };

  const initProductLightbox = (lightbox) => {
    if (lightbox.dataset.productLightboxBound === 'true') return;
    lightbox.dataset.productLightboxBound = 'true';

    const productSection = lightbox.previousElementSibling || doc;
    const viewport = qs('[data-lightbox-viewport]', lightbox);
    const slides = qsa('[data-lightbox-slide]', lightbox);
    const images = qsa('.product-lightbox__image', lightbox);
    const current = qs('[data-lightbox-current]', lightbox);
    const previous = qs('[data-lightbox-prev]', lightbox);
    const next = qs('[data-lightbox-next]', lightbox);
    const closeButtons = qsa('[data-lightbox-close]', lightbox);
    if (!viewport || !slides.length) return;

    let activeIndex = 0;
    let zoomLevel = 0;
    let panX = 0;
    let panY = 0;
    let lastTrigger = null;
    let pointerId = null;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOriginX = 0;
    let dragOriginY = 0;
    let hasDragged = false;

    const getActiveImage = () => qs('.product-lightbox__slide.is-active .product-lightbox__image', lightbox);
    const getZoomScale = () => [1, 1.75, 2.65][zoomLevel] || 1;

    const setZoomClasses = () => {
      lightbox.dataset.zoomLevel = String(zoomLevel);
      lightbox.classList.toggle('is-zoomed', zoomLevel > 0);
      lightbox.classList.toggle('is-zoom-max', zoomLevel === 2);
    };

    const clampPan = () => {
      const image = getActiveImage();
      if (!image || zoomLevel === 0) {
        panX = 0;
        panY = 0;
        return;
      }

      const scale = getZoomScale();
      const viewportRect = viewport.getBoundingClientRect();
      const maxX = Math.max(0, (image.offsetWidth * scale - viewportRect.width) / 2);
      const maxY = Math.max(0, (image.offsetHeight * scale - viewportRect.height) / 2);
      panX = clamp(panX, -maxX, maxX);
      panY = clamp(panY, -maxY, maxY);
    };

    const applyTransform = () => {
      const activeImage = getActiveImage();
      const scale = getZoomScale();
      clampPan();

      images.forEach((image) => {
        if (image === activeImage) {
          image.style.transform = `translate3d(${panX}px, ${panY}px, 0) scale(${scale})`;
        } else {
          image.style.transform = '';
          image.style.transformOrigin = '50% 50%';
        }
      });
    };

    const resetZoom = () => {
      zoomLevel = 0;
      panX = 0;
      panY = 0;
      images.forEach((image) => {
        image.style.transform = '';
        image.style.transformOrigin = '50% 50%';
      });
      setZoomClasses();
    };

    const setActive = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === activeIndex);
      });

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      resetZoom();
    };

    const open = (index, trigger) => {
      lastTrigger = trigger || null;
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      body.classList.add('product-lightbox-open');
      setActive(index);

      window.setTimeout(() => {
        const close = qs('.product-lightbox__close', lightbox);
        if (close) close.focus({ preventScroll: true });
      }, 40);
    };

    const close = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      body.classList.remove('product-lightbox-open');
      resetZoom();

      if (lastTrigger) lastTrigger.focus({ preventScroll: true });
      lastTrigger = null;
    };

    const setOriginFromEvent = (event) => {
      const image = getActiveImage();
      if (!image) return;

      const rect = image.getBoundingClientRect();
      const originX = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
      const originY = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
      image.style.transformOrigin = `${originX}% ${originY}%`;
    };

    const cycleZoom = (event) => {
      zoomLevel = zoomLevel >= 2 ? 0 : zoomLevel + 1;
      panX = 0;
      panY = 0;

      if (zoomLevel > 0) setOriginFromEvent(event);
      else {
        const image = getActiveImage();
        if (image) image.style.transformOrigin = '50% 50%';
      }

      setZoomClasses();
      applyTransform();
    };

    qsa('[data-lightbox-open]', productSection).forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const index = Number(trigger.dataset.lightboxIndex);
        if (Number.isNaN(index)) return;
        event.preventDefault();
        open(index, trigger);
      });
    });

    closeButtons.forEach((button) => button.addEventListener('click', close));
    if (previous) previous.addEventListener('click', () => setActive(activeIndex - 1));
    if (next) next.addEventListener('click', () => setActive(activeIndex + 1));

    viewport.addEventListener('pointerdown', (event) => {
      const image = event.target.closest('.product-lightbox__image');
      if (!image || !image.closest('.product-lightbox__slide.is-active')) return;

      pointerId = event.pointerId;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragOriginX = panX;
      dragOriginY = panY;
      hasDragged = false;

      if (zoomLevel > 0 && viewport.setPointerCapture) {
        try {
          viewport.setPointerCapture(pointerId);
        } catch (error) {
          return;
        }
      }
    });

    viewport.addEventListener('pointermove', (event) => {
      if (pointerId !== event.pointerId || zoomLevel === 0) return;

      const deltaX = event.clientX - dragStartX;
      const deltaY = event.clientY - dragStartY;
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) hasDragged = true;

      panX = dragOriginX + deltaX;
      panY = dragOriginY + deltaY;
      applyTransform();
    });

    const finishPointer = (event) => {
      if (pointerId !== event.pointerId) return;

      if (viewport.releasePointerCapture && typeof viewport.hasPointerCapture === 'function' && viewport.hasPointerCapture(pointerId)) {
        try {
          viewport.releasePointerCapture(pointerId);
        } catch (error) {
          // The pointer may already be released by the browser.
        }
      }

      if (!hasDragged) cycleZoom(event);
      pointerId = null;
    };

    viewport.addEventListener('pointerup', finishPointer);
    viewport.addEventListener('pointercancel', () => {
      pointerId = null;
      hasDragged = false;
    });

    doc.addEventListener('keydown', (event) => {
      if (!lightbox.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }

      if (event.key === 'ArrowLeft' && slides.length > 1) {
        event.preventDefault();
        setActive(activeIndex - 1);
      }

      if (event.key === 'ArrowRight' && slides.length > 1) {
        event.preventDefault();
        setActive(activeIndex + 1);
      }
    });

    window.addEventListener('resize', applyTransform, { passive: true });
    setActive(0);
  };

  const initProductZoomCursor = () => {
    if (doc.documentElement.dataset.productZoomCursorBound === 'true') return;
    doc.documentElement.dataset.productZoomCursorBound = 'true';

    const cursor = doc.createElement('span');
    cursor.className = 'product-hover-zoom-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    body.appendChild(cursor);

    let activeTarget = null;

    const moveCursor = (event) => {
      cursor.style.left = `${event.clientX}px`;
      cursor.style.top = `${event.clientY}px`;
    };

    const hideCursor = () => {
      if (activeTarget) activeTarget.classList.remove('is-cursor-active');
      activeTarget = null;
      cursor.classList.remove('is-visible');
    };

    doc.addEventListener('pointerover', (event) => {
      const target = event.target.closest('.product-media__button');
      if (!target || !desktop.matches || event.pointerType === 'touch') return;

      activeTarget = target;
      activeTarget.classList.add('is-cursor-active');
      moveCursor(event);
      cursor.classList.add('is-visible');
    });

    doc.addEventListener('pointermove', (event) => {
      if (!activeTarget) return;
      moveCursor(event);
    });

    doc.addEventListener('pointerout', (event) => {
      if (!activeTarget || activeTarget.contains(event.relatedTarget)) return;
      hideCursor();
    });

    onMediaChange(desktop, hideCursor);
  };

  const init = (scope = doc) => {
    const galleries = qsa('[data-product-gallery]', scope);
    const lightboxes = qsa('[data-product-lightbox]', scope);
    if (!galleries.length && !lightboxes.length && !qs('[data-product-gallery]') && !qs('[data-product-lightbox]')) return;

    galleries.forEach(initProductGallery);
    lightboxes.forEach(initProductLightbox);

    if (qs('.product-media__button')) {
      initProductZoomCursor();
    }
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  doc.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* ==========================================================================
   Mega menu
   Rides on the existing drawer system: the sheet is a [data-drawer="menu"], so
   opening and closing stays with the code above. This only adds the column
   behaviour on top.
   ========================================================================== */

(() => {
  const doc = document;
  const desktop = window.matchMedia('(min-width: 990px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (sel, root = doc) => root.querySelector(sel);
  const qsa = (sel, root = doc) => Array.from(root.querySelectorAll(sel));

  const initMegaMenu = (el) => {
    if (el.dataset.megaBound === 'true') return;
    el.dataset.megaBound = 'true';

    const nav = qs('[data-mega-nav]', el);
    const sheet = qs('[data-mega-sheet]', el);
    const items = qsa('[data-mega-item]', el);
    const panels = qsa('[data-mega-panel]', el);
    const tileGroups = qsa('[data-mega-tiles]', el);
    const back = qs('[data-mega-back]', el);
    const getBaseLine = () => (doc.body.classList.contains('pre-collect-theme') ? 'pre-collect' : 're-collect');

    let hoverTimer = null;
    let activeKey = null;
    el.setAttribute('data-line-scope', getBaseLine());

    const getResponsiveWidth = (min, ratio, max) =>
      Math.min(Math.max(min, window.innerWidth * ratio), max);

    const setSheetWidth = (state = 'primary') => {
      if (!sheet) return;
      if (!desktop.matches) {
        sheet.style.width = '100vw';
        return;
      }

      const primaryWidth = getResponsiveWidth(340, 0.295, 1040);
      const subWidth = state === 'sub' || state === 'tiles' ? getResponsiveWidth(280, 0.23, 760) : 0;
      const tilesWidth = state === 'tiles' ? getResponsiveWidth(340, 0.295, 1040) : 0;
      sheet.style.width = `${Math.min(primaryWidth + subWidth + tilesWidth, window.innerWidth)}px`;
    };

    const findGroup = (list, key) =>
      list.find(
        (node) =>
          node.getAttribute('data-mega-panel') === key || node.getAttribute('data-mega-tiles') === key
      ) || null;

    const activate = (key) => {
      if (activeKey === key) return;
      activeKey = key;

      const panel = findGroup(panels, key);
      const tiles = findGroup(tileGroups, key);

      items.forEach((item) => {
        const isActive = item.getAttribute('data-mega-item') === key;
        item.classList.toggle('is-active', isActive);
        const link = qs('[data-mega-trigger]', item);
        if (link && link.hasAttribute('aria-haspopup')) {
          link.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        }
        if (isActive) {
          el.setAttribute('data-line-scope', item.getAttribute('data-item-line') || getBaseLine());
        }
      });

      panels.forEach((group) => group.classList.toggle('is-active', group === panel));
      tileGroups.forEach((group) => group.classList.toggle('is-active', group === tiles));

      if (nav) nav.classList.add('has-active');
      el.classList.toggle('is-sub-open', Boolean(panel));
      el.classList.toggle('is-tiles-open', Boolean(tiles));
      setSheetWidth(tiles ? 'tiles' : panel ? 'sub' : 'primary');
    };

    const deactivate = () => {
      activeKey = null;
      items.forEach((item) => {
        item.classList.remove('is-active');
        const link = qs('[data-mega-trigger]', item);
        if (link && link.hasAttribute('aria-haspopup')) link.setAttribute('aria-expanded', 'false');
      });
      panels.forEach((group) => group.classList.remove('is-active'));
      tileGroups.forEach((group) => group.classList.remove('is-active'));
      if (nav) nav.classList.remove('has-active');
      el.classList.remove('is-sub-open', 'is-tiles-open');
      el.setAttribute('data-line-scope', getBaseLine());
      setSheetWidth('primary');
    };

    items.forEach((item) => {
      const key = item.getAttribute('data-mega-item');
      const hasPanel = item.getAttribute('data-mega-has-panel') === 'true';
      const link = qs('[data-mega-trigger]', item);

      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !desktop.matches) return;
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(hasPanel ? () => activate(key) : deactivate, hasPanel ? 70 : 140);
      });

      item.addEventListener('pointerleave', () => clearTimeout(hoverTimer));

      if (!link) return;

      /* Tabbing through the menu shows what the mouse would show */
      link.addEventListener('focus', () => {
        if (!desktop.matches) return;
        if (hasPanel) activate(key);
        else deactivate();
      });

      /* On touch and narrow screens the first tap opens the column; the
         panel's own "View all" is how you reach the collection itself. */
      link.addEventListener('click', (event) => {
        if (!hasPanel || desktop.matches) return;
        event.preventDefault();
        activate(key);
      });
    });

    if (back) back.addEventListener('click', deactivate);

    window.addEventListener('resize', () => {
      if (!el.classList.contains('is-open')) return;
      if (el.classList.contains('is-tiles-open')) {
        setSheetWidth('tiles');
      } else if (el.classList.contains('is-sub-open')) {
        setSheetWidth('sub');
      } else {
        setSheetWidth('primary');
      }
    }, { passive: true });

    el.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (!desktop.matches && activeKey) {
        event.stopPropagation();
        deactivate();
      }
    });

    /* The drawer code above owns opening and closing; watch the class it sets
       so a reopened menu always starts from a clean state. */
    let wasOpen = el.classList.contains('is-open');
    const observer = new MutationObserver(() => {
      const isOpen = el.classList.contains('is-open');

      if (isOpen && !wasOpen) {
        clearTimeout(hoverTimer);
        deactivate();
        setSheetWidth('primary');
        wasOpen = true;
        return;
      }

      if (!isOpen && wasOpen) {
        clearTimeout(hoverTimer);
        setTimeout(() => {
          if (!el.classList.contains('is-open')) deactivate();
        }, 520);
        wasOpen = false;
      }
    });
    observer.observe(el, { attributes: true, attributeFilter: ['class'] });
  };

  const initMegaPromo = (el) => {
    if (el.dataset.promoBound === 'true') return;
    el.dataset.promoBound = 'true';

    const slides = qsa('[data-mega-promo-slide]', el);
    const dots = qsa('[data-mega-promo-dot]', el);
    const caption = qs('[data-mega-promo-caption]', el);
    if (slides.length < 2) return;

    let index = 0;
    let timer = null;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (caption) caption.textContent = slides[index].getAttribute('data-caption') || '';
    };

    const stop = () => timer && clearInterval(timer);
    const start = () => {
      if (reduceMotion) return;
      stop();
      timer = setInterval(() => show(index + 1), 5000);
    };

    dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        show(i);
        start();
      })
    );

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
    show(0);
    start();
  };

  const init = (scope = doc) => {
    qsa('[data-mega]', scope).forEach(initMegaMenu);
    qsa('[data-mega-promo]', scope).forEach(initMegaPromo);
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  /* The editor re-renders the header when its settings change */
  doc.addEventListener('shopify:section:load', (event) => init(event.target));
})();

/* ==========================================================================
   Announcement bar and tactile motion
   ========================================================================== */

(() => {
  const doc = document;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const qs = (sel, root = doc) => root.querySelector(sel);
  const qsa = (sel, root = doc) => Array.from(root.querySelectorAll(sel));

  const initAnnouncement = (el) => {
    if (el.dataset.announcementBound === 'true') return;
    el.dataset.announcementBound = 'true';

    const slides = qsa('[data-announcement-item]', el);
    if (slides.length < 2) return;

    let index = 0;
    let timer = null;
    const interval = Number(el.dataset.interval) || 5000;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === index);
      });
    };

    const stop = () => {
      if (timer) window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      if (reduceMotion) return;
      stop();
      timer = window.setInterval(() => show(index + 1), interval);
    };

    qsa('[data-announcement-next]', el).forEach((button) => {
      button.addEventListener('click', () => {
        show(index + 1);
        start();
      });
    });

    qsa('[data-announcement-prev]', el).forEach((button) => {
      button.addEventListener('click', () => {
        show(index - 1);
        start();
      });
    });

    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', start);
    show(0);
    start();
  };

  const bindPressFeedback = () => {
    doc.addEventListener('pointerdown', (event) => {
      const target = event.target.closest('.button, .header-button, .icon-button, .text-button, .mega-menu__link, .product-card__media, summary');
      if (!target) return;

      target.classList.add('is-pressing');
      window.setTimeout(() => target.classList.remove('is-pressing'), 220);
    });
  };

  const init = (scope = doc) => {
    qsa('[data-announcement]', scope).forEach(initAnnouncement);
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => {
      init();
      bindPressFeedback();
    }, { once: true });
  } else {
    init();
    bindPressFeedback();
  }

  doc.addEventListener('shopify:section:load', (event) => init(event.target));
})();
